import { lazy, Suspense, useEffect, useState } from "react";
import type { DailyRecord, LayerKey, Manifest, ScoreFields } from "../types/forecast";
import {
  abundanceCategory,
  categoryRank,
  dataQualityCategory,
  DATA_QUALITY_CATEGORIES,
  exposureForActivity,
  finalRiskForActivity,
  formatScore,
  riskCategory,
} from "../lib/riskModel";
import { computeAdjustedRisk } from "../lib/reportAdjustment";
import { getReportSummary, isReportingConfigured, type ReportSummary } from "../lib/reportsApi";
import type { LocationSeries } from "../hooks/useForecastData";
import { useI18n } from "../i18n";
import type { I18nKey } from "../i18n/types";
import { currentDateIso, currentHourBucketLabel, formatStockholmDateLabel, formatStockholmHourShort, hourBucketToDate } from "../lib/time";
import { ReportForm } from "./ReportForm";
import { HourTimeline } from "./HourTimeline";
import { ForecastCards } from "./ForecastCards";
import { DaySelector } from "./DaySelector";

// recharts is a sizeable dependency (see vite.config.ts manualChunks) --
// deferring its import until a chart is actually about to render (rather
// than eagerly at LocationPanel module load) keeps it out of the initial
// bundle/network path entirely for users who never open a location.
const SevenDayChart = lazy(() => import("./RiskCharts").then((m) => ({ default: m.SevenDayChart })));
const HourlyChart = lazy(() => import("./RiskCharts").then((m) => ({ default: m.HourlyChart })));

function ChartSkeleton() {
  return <div className="skeleton skeleton-chart" aria-hidden="true" />;
}

function addDaysIso(dateIso: string, days: number): string {
  const d = new Date(dateIso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export interface LocationPanelProps {
  placeName: string;
  latitude: number;
  longitude: number;
  cellId: string | null;
  manifest: Manifest | null;
  activity: string;
  layer: LayerKey;
  activeRecord: ScoreFields | null;
  activeDailyRecord: DailyRecord | null;
  isHourlyDay: boolean;
  hourLabel: string | null;
  dayIndex: number;
  daypart: string;
  date: string;
  dayOptions: string[];
  onDateChange: (date: string) => void;
  series: LocationSeries | null;
  loading: boolean;
  error: string | null;
  onShare: () => void;
  shareCopied: boolean;
}

function SubscoreCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="subscore-card">
      <div className="label">{label}</div>
      <div className="value">{formatScore(value)}</div>
    </div>
  );
}

function DataQualityMeter({ activeIndex }: { activeIndex: number }) {
  return (
    <div className="quality-meter" aria-hidden="true">
      {DATA_QUALITY_CATEGORIES.map((c, i) => (
        <span key={c.key} className={`quality-seg${i <= activeIndex ? " filled" : ""}`} style={{ background: i <= activeIndex ? c.color : undefined }} />
      ))}
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="panel-content" aria-hidden="true">
      <div className="skeleton skeleton-line skeleton-title" />
      <div className="skeleton skeleton-line skeleton-subtitle" />
      <div className="skeleton skeleton-hero" />
      <div className="subscore-grid">
        <div className="skeleton skeleton-card" />
        <div className="skeleton skeleton-card" />
        <div className="skeleton skeleton-card" />
      </div>
      <div className="skeleton skeleton-block" />
    </div>
  );
}

export function LocationPanel({
  placeName,
  latitude,
  longitude,
  cellId,
  manifest,
  activity,
  layer,
  activeRecord,
  activeDailyRecord,
  isHourlyDay,
  hourLabel,
  date,
  dayOptions,
  onDateChange,
  series,
  loading,
  error,
  onShare,
  shareCopied,
}: LocationPanelProps) {
  const { t, locale } = useI18n();
  const [reportOpen, setReportOpen] = useState(false);
  const [reportSummary, setReportSummary] = useState<ReportSummary | null>(null);
  const [justSubmitted, setJustSubmitted] = useState(false);

  useEffect(() => {
    setReportSummary(null);
    if (!cellId || !isReportingConfigured()) return;
    let cancelled = false;
    getReportSummary(cellId).then((result) => {
      if (!cancelled && result.ok) setReportSummary(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, [cellId, justSubmitted]);

  if (loading && !activeRecord) {
    return <PanelSkeleton />;
  }

  if (error) {
    return (
      <div className="panel-content">
        <p role="alert">{t("panel.loadError", { error })}</p>
      </div>
    );
  }

  if (!activeRecord) {
    return (
      <div className="panel-empty">
        <p>{t("panel.empty")}</p>
      </div>
    );
  }

  const activityMultiplier = manifest?.activities?.[activity] ?? 1.0;
  const adjustedFinalRisk = finalRiskForActivity(
    activeRecord.population_potential,
    activeRecord.biting_activity,
    activeRecord.base_exposure_fraction,
    activityMultiplier,
    manifest?.combination
  );

  // Exactly two products are exposed in the UI now: Myggrisk ("am I likely
  // to get bitten") and Myggläge ("how favourable is this area in
  // general") -- see types/forecast.ts::SIMPLE_PRODUCT_KEYS. Anything else
  // (a legacy bookmarked link carrying the old current_risk/biting_activity/
  // confidence layer values) falls back to the risk treatment rather than
  // breaking, since that's the more universally-meaningful default.
  const isAbundanceLayer = layer === "population_potential";
  const isRiskProduct = !isAbundanceLayer;

  const displayValue = isAbundanceLayer ? activeRecord.population_potential : adjustedFinalRisk;
  const dqCategory = dataQualityCategory(activeRecord.confidence);
  const dqLabel = t(`dataQuality.${dqCategory}` as I18nKey);
  const dqIndex = DATA_QUALITY_CATEGORIES.findIndex((c) => c.key === dqCategory);
  // Myggläge uses its own category bounds (see riskModel.ts::abundanceCategory)
  // -- reusing risk's 0/20/40/60/80 bounds left "very_high" permanently
  // empty for this product (docs/model-audit-after.md).
  const riskLikeCategory = isAbundanceLayer
    ? abundanceCategory(displayValue, manifest?.thresholds?.abundance)
    : riskCategory(displayValue);
  const riskLikeLabel = t(`risk.category.${riskLikeCategory.key}` as I18nKey);

  const reportAdjustment = computeAdjustedRisk(adjustedFinalRisk, reportSummary);

  // Day-level context only -- "Idag" / "Imorgon" / "Lördag 8 aug" -- never
  // an hour, since both remaining products always describe the whole day,
  // not a selected moment (see App.tsx).
  const isToday = date === currentDateIso();
  const isTomorrow = date === addDaysIso(currentDateIso(), 1);
  const timeContext = isToday ? t("controlBar.today") : isTomorrow ? t("controlBar.tomorrow") : formatStockholmDateLabel(date, locale);
  // Lowercase, mid-sentence form of the same day context -- "bettrisk idag",
  // "hogst risk imorgon" -- used wherever the day is interpolated into a
  // sentence rather than standing alone as a label.
  const dayPhraseLower = isToday || isTomorrow ? timeContext.toLowerCase() : timeContext;

  // Per-day risk category for the new 7-day selector strip (item 2 of the
  // public-launch UX pass) -- reuses the already-fetched `series.daily`
  // (same data ForecastCards/SevenDayChart below already use) rather than
  // fetching anything new.
  const dailyByDate = series ? new Map(series.daily.map((d) => [d.date, d])) : null;

  // "Now vs. later today" (item 7): find the hourly record matching the
  // actual current instant (not a UI hour selector -- that no longer
  // exists) and compare its category to today's peak category. Only
  // "meaningful" (category change) is worth a sentence -- a same-category
  // drift like 14 -> 16 would just be noise. Today only: "now" isn't a
  // coherent concept for a future day.
  const nowHourly =
    isToday && isRiskProduct && series ? series.hourly.find((h) => h.hourLabel === currentHourBucketLabel()) : null;
  const nowRisk = nowHourly
    ? finalRiskForActivity(
        nowHourly.population_potential,
        nowHourly.biting_activity,
        nowHourly.base_exposure_fraction,
        activityMultiplier,
        manifest?.combination
      )
    : null;
  const nowCategory = nowRisk !== null ? riskCategory(nowRisk) : null;
  const peakCategoryForToday = activeDailyRecord ? riskCategory(activeDailyRecord.risk) : null;
  const peakHour = activeDailyRecord?.daily_peak_local_time
    ? parseInt(activeDailyRecord.daily_peak_local_time.slice(0, 2), 10)
    : null;
  const currentHour = parseInt(formatStockholmHourShort(currentHourBucketLabel(), locale), 10);
  const peakStillAhead = peakHour !== null && Number.isFinite(currentHour) && peakHour > currentHour;
  const nowVsPeakNote =
    nowCategory && peakCategoryForToday && peakHour !== null && nowCategory.key !== peakCategoryForToday.key && peakStillAhead
      ? t("panel.nowVsPeak", {
          nowCategory: t(`risk.category.${nowCategory.key}` as I18nKey),
          peakCategory: t(`risk.category.${peakCategoryForToday.key}` as I18nKey).toLowerCase(),
          time: String(peakHour),
        })
      : null;

  // "Högst risk idag: kl 20" -- honestly daypart-resolution (not a precise
  // minute), from the daily record's own daily_peak_local_time (already
  // Stockholm-local, see pipeline.py). Risk-product only: Myggläge
  // shouldn't imply it has an hourly peak the way a weather-driven nuisance
  // score does. Superseded by nowVsPeakNote above when there's an actual
  // category change ahead today -- that sentence already carries the peak
  // time, so showing both would repeat the same "kl X" twice.
  const peakAroundNote =
    isRiskProduct && !nowVsPeakNote && activeDailyRecord?.daily_peak_local_time
      ? t("panel.peakAroundTime", { day: dayPhraseLower, time: activeDailyRecord.daily_peak_local_time.slice(0, 2) })
      : null;

  // Myggrisk-vs-Myggläge relationship (item 6): the two products can
  // genuinely disagree (lots of habitat but suppressed activity, or little
  // habitat but favourable activity conditions right now) -- built purely
  // from the two already-computed category tiers, shown whenever they
  // diverge by 2+ tiers regardless of which product is currently selected
  // (it's inherently a statement about both).
  const abundanceCategoryForRelationship = activeRecord ? abundanceCategory(activeRecord.population_potential, manifest?.thresholds?.abundance) : null;
  const riskCategoryForRelationship = riskCategory(adjustedFinalRisk);
  const relationshipNote = (() => {
    if (!abundanceCategoryForRelationship) return null;
    const gap = categoryRank(abundanceCategoryForRelationship.key) - categoryRank(riskCategoryForRelationship.key);
    if (gap >= 2) return t("panel.relationship.abundanceHigherThanRisk");
    if (gap <= -2) {
      return t("panel.relationship.riskHigherThanAbundance", {
        abundanceCategory: t(`risk.category.${abundanceCategoryForRelationship.key}` as I18nKey).toLowerCase(),
      });
    }
    return null;
  })();

  return (
    <div className="panel-content">
      <div>
        {/* Coordinates deliberately do not appear here (item 3 of the
            public-launch UX pass): the primary result should read as a
            consumer forecast, not a GIS readout. They're still available,
            just relocated into "Tekniska detaljer" below. Myggläge has no
            hero kicker of its own (see below), so it keeps a plain day
            label here instead -- Myggrisk's day context lives in the
            kicker ("Bettrisk idag") instead, to avoid saying it twice. */}
        <div className="location-name">{placeName}</div>
        {isAbundanceLayer && <div className="location-subline">{timeContext}</div>}
      </div>

      <DaySelector
        dayOptions={dayOptions}
        date={date}
        onDateChange={onDateChange}
        dailyByDate={dailyByDate}
        isAbundanceLayer={isAbundanceLayer}
        activityMultiplier={activityMultiplier}
        combination={manifest?.combination}
        abundanceThresholds={manifest?.thresholds?.abundance}
      />

      {/* Hero: the answer to "will mosquitoes bother me?" first, in plain
          language and large type -- the recommendation sentence is the
          hero, not the number (see the "simplify around the user's mental
          model" iteration). Technical figures live in "Tekniska detaljer"
          further down, never up here. */}
      <div className="hero-block">
        {/* Kicker first (item 1 + item 8): "Bettrisk idag" establishes what
            the big word below actually means before the user even reads
            it, making the Myggrisk = bettrisk equivalence explicit at the
            very top of the screen rather than something to infer. */}
        {isRiskProduct && <div className="hero-kicker">{t("panel.riskKicker", { day: dayPhraseLower })}</div>}
        <div className={`hero-headline${isRiskProduct ? " hero-headline--risk" : ""}`} style={{ color: riskLikeCategory.color }}>
          {isAbundanceLayer ? t(`panel.abundanceHeadline.${riskLikeCategory.key}` as I18nKey) : riskLikeLabel}
        </div>

        <p className="hero-guidance">
          {t(`panel.${isAbundanceLayer ? "abundanceAdvice" : "advice"}.${riskLikeCategory.key}` as I18nKey)}
        </p>

        {isRiskProduct && nowVsPeakNote && <p className="hero-subnote">{nowVsPeakNote}</p>}
        {isRiskProduct && peakAroundNote && <p className="hero-subnote">{peakAroundNote}</p>}
        {isAbundanceLayer && <p className="hero-subnote">{t("panel.abundanceExplain")}</p>}

        {/* Myggrisk vs Myggläge relationship (item 6) -- shown regardless of
            which product is currently selected, since it's inherently a
            statement about both at once and is one of the most useful
            things this app can say when they genuinely disagree. */}
        {relationshipNote && <p className="hero-relationship">{relationshipNote}</p>}
      </div>

      {/* "When is it worst?" -- a compact dawn-to-night dot strip for the
          selected day, only meaningful while hourly data exists (today/
          tomorrow); days 2-6 only have daily-resolution data. */}
      {isRiskProduct && isHourlyDay && series && series.hourly.length > 0 && (
        <HourTimeline
          hourly={series.hourly}
          date={date}
          activityMultiplier={activityMultiplier}
          combination={manifest?.combination}
        />
      )}

      {/* "Tomorrow" / weekend planning -- plain today/tomorrow/day-after
          cards instead of making everyone read a line chart to plan ahead. */}
      {isRiskProduct && series && series.daily.length > 0 && (
        <ForecastCards daily={series.daily} activityMultiplier={activityMultiplier} combination={manifest?.combination} />
      )}

      {/* Everything below is progressive disclosure: collapsed by default,
          for the minority of visitors who want the methodology or the raw
          numbers rather than just the headline/recommendation above. */}

      {/* Myggläge deliberately has NO factor list here: the generated
          explanation mixes population, activity and exposure factors
          (including wind/rain suppression), which would misleadingly read
          as if current weather affects abundance -- see
          panel.abundanceExplain in the hero block instead. */}
      {((activeDailyRecord && isRiskProduct) || (series && (series.daily.length > 0 || series.hourly.length > 0))) && (
        <details className="disclosure">
          <summary>{t("panel.howItWorksTitle")}</summary>
          <div className="disclosure-content">
            {activeDailyRecord && isRiskProduct && (
              <div>
                <div className="section-title">{t("panel.whyTitleToday")}</div>
                <p style={{ margin: 0, fontSize: "0.9rem" }}>{activeDailyRecord.explanation.summary}</p>
                <ul className="factor-list">
                  {activeDailyRecord.explanation.positive_factors.map((f) => (
                    <li className="positive" key={f.key}>
                      <span className="sign">+</span> {f.label}
                    </li>
                  ))}
                  {activeDailyRecord.explanation.negative_factors.map((f) => (
                    <li className="negative" key={f.key}>
                      <span className="sign">-</span> {f.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {series && series.daily.length > 0 && (
              <div>
                <div className="section-title">{t("panel.next7days")}</div>
                <Suspense fallback={<ChartSkeleton />}>
                  <SevenDayChart
                    daily={series.daily}
                    activityMultiplier={activityMultiplier}
                    combination={manifest?.combination}
                    isAbundanceLayer={isAbundanceLayer}
                    abundanceThresholds={manifest?.thresholds?.abundance}
                  />
                </Suspense>
              </div>
            )}

            {series && series.hourly.length > 0 && (
              <div>
                <div className="section-title">{t("panel.next48h")}</div>
                <Suspense fallback={<ChartSkeleton />}>
                  <HourlyChart
                    hourly={series.hourly}
                    activityMultiplier={activityMultiplier}
                    combination={manifest?.combination}
                    isAbundanceLayer={isAbundanceLayer}
                    abundanceThresholds={manifest?.thresholds?.abundance}
                  />
                </Suspense>
              </div>
            )}
          </div>
        </details>
      )}

      <details className="disclosure">
        <summary>{t("panel.detailsTitle")}</summary>
        <div className="disclosure-content technical-section">
          <p className="model-disclaimer">{t("panel.modelDisclaimer")}</p>

          <p className="index-line">
            {t("panel.coordinatesLabel", { lat: latitude.toFixed(4), lon: longitude.toFixed(4) })}
          </p>
          <p className="index-line">{t("panel.indexLabel", { value: formatScore(displayValue) })}</p>

          {isRiskProduct && (
            <p style={{ fontSize: "0.78rem", color: "var(--color-text-muted)" }}>
              {t("panel.activityAdjusted", { activity: t(`activity.${activity}` as I18nKey) })}
            </p>
          )}

          {reportAdjustment.applied && (
            <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
              {t("panel.modelEstimate", {
                model: formatScore(reportAdjustment.modelRisk),
                count: reportSummary?.report_count ?? 0,
                adjusted: formatScore(reportAdjustment.adjustedRisk),
                weight: Math.round(reportAdjustment.weight * 100),
              })}
            </p>
          )}

          <div className="subscore-grid">
            <SubscoreCard label={t("panel.population")} value={activeRecord.population_potential} />
            <SubscoreCard label={t("panel.activity")} value={activeRecord.biting_activity} />
            <SubscoreCard
              label={t("panel.exposure")}
              value={exposureForActivity(activeRecord.base_exposure_fraction, activityMultiplier)}
            />
          </div>

          <div>
            <div className="section-title">{t("panel.dataQualityTitle")}</div>
            <div className="quality-row">
              <DataQualityMeter activeIndex={dqIndex} />
              <span className="quality-label">{dqLabel}</span>
            </div>
            <p className="quality-explain">{t("panel.dataQualityExplain")}</p>
          </div>
        </div>
      </details>

      {/* Sharing is the everyday consumer action; contributing a report is a
          more specialised secondary one (item 5 of the public-launch UX
          pass) -- share leads, both visually and in DOM order. */}
      <div className="button-row">
        <button type="button" className="button primary" onClick={onShare}>
          {shareCopied ? t("panel.shareCopied") : t("panel.shareButton")}
        </button>
        <button type="button" className="button" onClick={() => setReportOpen(true)}>
          {t("panel.reportButton")}
        </button>
      </div>

      {!isReportingConfigured() && (
        <p style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>{t("panel.offlineDemo")}</p>
      )}

      {reportOpen && cellId && (
        <ReportForm
          cellId={cellId}
          latitude={latitude}
          longitude={longitude}
          forecastScore={adjustedFinalRisk}
          modelVersion={manifest?.model_version ?? "unknown"}
          forecastWindMs={activeRecord.forecast_wind_ms}
          effectiveWindMs={activeRecord.effective_wind_ms}
          temperatureC={activeRecord.temperature_c}
          humidityPct={activeRecord.humidity_pct}
          populationPotential={activeRecord.population_potential}
          bitingActivity={activeRecord.biting_activity}
          targetTimestamp={
            isHourlyDay && hourLabel ? hourBucketToDate(hourLabel).toISOString() : date ? `${date}T00:00:00Z` : undefined
          }
          onClose={() => setReportOpen(false)}
          onSubmitted={() => {
            setReportOpen(false);
            setJustSubmitted((v) => !v);
          }}
        />
      )}
    </div>
  );
}
